// @ts-strict-ignore
import * as React from 'react';
import { useDispatch } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import { AppRouteComponentMatchParams, ReleaseComponentProps } from '../../typescriptHelpers';
import { titleStyle, runwayColors } from '../styles/globalStyles';
import TargetDatesSection from './DatesSection';
import ProductSprintSection from './ProductSprintSection';
import { ReleasePilotWidget } from '@/components/appHome/widgets/ReleasePilotWidget';
import CodeSection from './CodeSection';
import {
    ReleaseNav,
    ReleaseStatus,
    ReleaseStepType,
    ReleaseTimelinePhase,
    Statistic,
    StatisticType,
} from '../../types';
import Divider from 'antd/lib/divider';
import { useSelector } from 'react-redux';
import {
    selectIsDemoModeEnabledForRelease,
    selectRelease,
    selectAppNeedsIntegrationsSetup,
    selectIsAppsDemoModeEnabled,
    selectReleaseType,
    selectReleaseVersion,
    selectRollbackTargetVersionInfo,
    selectReleaseOverview,
    selectIsWorkflowsCIStepEnabled,
} from '../../selectors/selectors';
import { selectAveragedReleaseStatistics } from '../../selectors/memoizedSelectors';
import { RootState } from '../../reducers/reducers';
import ReleaseDemoView, { ReleaseDemoBannerPlacement } from '../releaseSteps/ReleaseDemoView';
import { trackReleaseRouteEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import HighlightsSection from './HighlightsSection';
import DestinationsBanner from './DestinationsBanner';
import { tryFetchEntity } from '../../actions/entities';
import { FetchableEntityType } from '../../entityType';
import { globalMessage } from '../../utils/globalMessage';
import ReleaseActionsComponent from '../sharedComponents/ReleaseActionsComponent';
import Layout from 'antd/lib/layout';
import { EmptyStepBanner } from '../releaseEmptyState/EmptyStateBanner';
import useSetLastViewedReleaseStep from '../hooks/useSetLastViewedReleaseStep';
import Alert from 'antd/lib/alert';
import { Link, useParams } from 'react-router-dom';
import { ReleaseStepTabType, createReleaseStepTabRoute } from '../../helpers/routeHelpers';
import { UndoOutlined } from '@ant-design/icons';
import { useError, useIsLoading } from '@/utils/hooks/helpers';

const ReleaseOverviewContainer: React.FC<ReleaseComponentProps> = (props: ReleaseComponentProps) => {
    const { orgId, appId, releaseId } = props;
    const pageTitle = 'Overview';
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const dispatch = useDispatch();

    React.useEffect(() => {
        document.title = 'Overview';
    }, []);

    const isDemoModeEnabled = useSelector((state: RootState) =>
        selectIsDemoModeEnabledForRelease(state, props.releaseId),
    );
    const isAppsDemoModeEnabled = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const releasePilotId = useSelector((state: RootState) => selectRelease(state, releaseId).releasePilotId);
    const releaseStatus = useSelector((state: RootState) => selectRelease(state, releaseId).status);
    const canBeSubmitted = useSelector((state: RootState) => selectRelease(state, releaseId).canBeSubmitted);
    const canRevertCompletedStatus = useSelector(
        (state: RootState) => selectRelease(state, releaseId).canRevertCompletedStatus,
    );
    const releaseTimelinePhase = useSelector((state: RootState) => selectRelease(state, releaseId).timelinePhase);
    const needsIntegrationSetup = useSelector((state: RootState) =>
        selectAppNeedsIntegrationsSetup(state, props.appId),
    );
    const isCompletedRelease = releaseStatus === ReleaseStatus.Completed;

    const isLoadingReleaseStatsHistory = useIsLoading(FetchableEntityType.GetReleaseOverview, releaseId);
    const fetchReleaseStatsHistoryError = useError(FetchableEntityType.GetReleaseOverview, releaseId);
    const { releaseStatsByTypes, currentReleaseStats } = useSelector((state: RootState) =>
        selectAveragedReleaseStatistics(state, props.releaseId),
    );
    const rollbackInfo = useSelector((state: RootState) => selectRelease(state, props.releaseId).rollbackInfo);
    const destinationSummaries = useSelector(
        (state: RootState) => selectRelease(state, props.releaseId).destinationSummaries,
    );

    const releaseOverview = useSelector((state: RootState) => selectReleaseOverview(state, props.releaseId));

    const statistics =
        currentReleaseStats?.length > 0 ?
            currentReleaseStats
        :   Object.keys(StatisticType).map(
                (key) =>
                    ({
                        type: key as StatisticType,
                    }) as Statistic,
            );

    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const version = useSelector((state: RootState) => selectReleaseVersion(state, props.releaseId));
    const releaseType = useSelector((state: RootState) => selectReleaseType(state, props.releaseId));
    const rollbackTargetVersionInfo = useSelector((state: RootState) =>
        selectRollbackTargetVersionInfo(state, props.releaseId),
    );

    React.useEffect(() => {
        if (appId && releaseId && !releaseOverview && !isViewingAppsInDemoMode) {
            dispatch(tryFetchEntity(FetchableEntityType.GetReleaseOverview, { orgId, appId, releaseId }, releaseId));
        }
    }, [appId, releaseId, releaseOverview, isViewingAppsInDemoMode]);

    React.useEffect(() => {
        if (fetchReleaseStatsHistoryError) {
            globalMessage.error(fetchReleaseStatsHistoryError);
        }
    }, [fetchReleaseStatsHistoryError]);

    const showDemoBanner =
        ((needsIntegrationSetup && !isCompletedRelease) || isDemoModeEnabled) && !isAppsDemoModeEnabled;

    useSetLastViewedReleaseStep(releaseId, ReleaseNav.Overview);

    // track view release overview event
    React.useEffect(() => {
        dispatch(
            trackReleaseRouteEvent({
                orgId: orgId,
                appId: appId,
                releaseId: releaseId,
                eventName: AnalyticEvents.Event.ViewReleaseOverview,
            }),
        );
    }, []);

    const releaseActionsInactive = releaseStatus === ReleaseStatus.Skipped || releaseStatus === ReleaseStatus.Completed;
    const skipReleaseDisabled = releaseActionsInactive || releaseTimelinePhase !== ReleaseTimelinePhase.Current;

    const releaseHasActiveRollback = !!rollbackInfo && rollbackInfo.status == ReleaseStatus.Active;
    const rollbackReleaseRoute =
        releaseHasActiveRollback ?
            createReleaseStepTabRoute(
                orgIdParam,
                appIdParam,
                rollbackInfo.version,
                ReleaseStepType.RollbackBuild,
                ReleaseStepTabType.Summary,
            )
        :   null;

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    return (
        <Layout
            style={{
                backgroundColor: runwayColors.white,
                position: 'relative',
            }}
        >
            <EmptyStepBanner
                appId={props.appId}
                releaseId={props.releaseId}
                releaseStepType={null}
                customMessage={`The Overview page surfaces a number of key statistics that are relevant throughout the course of your release. Target dates, a detailed timeline, contribution statistics, and more, are all available here.`}
                isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
            />
            <div
                style={{
                    padding: '24px 36px 95px 36px',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {showDemoBanner && (
                    <ReleaseDemoView
                        releaseId={props.releaseId}
                        orgId={props.orgId}
                        appId={props.appId}
                        placement={ReleaseDemoBannerPlacement.Overview}
                    />
                )}
                {destinationSummaries && destinationSummaries.length > 0 && (
                    <DestinationsBanner destinationSummaries={destinationSummaries} />
                )}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                    }}
                >
                    <Text style={titleStyle} type="secondary">
                        {pageTitle}
                    </Text>
                    <ReleaseActionsComponent
                        orgId={props.orgId}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        version={version}
                        releaseType={releaseType}
                        releaseStatus={releaseStatus}
                        editReleaseDisabled={releaseActionsInactive}
                        deleteReleaseDisabled={releaseActionsInactive}
                        skipReleaseDisabled={skipReleaseDisabled}
                        rollbackTargetVersionInfo={rollbackTargetVersionInfo}
                        canBeSubmitted={canBeSubmitted}
                        canRevertCompletedStatus={canRevertCompletedStatus}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    {releasePilotId != null && (
                        <>
                            <div style={{ margin: 0, padding: 10, flexShrink: 0 }}>
                                <ReleasePilotWidget
                                    appId={appIdParam}
                                    releasePilotId={releasePilotId}
                                    onReleaseOverview={true}
                                />
                            </div>
                            <Divider type="vertical" style={{ height: '75%', margin: '10px 10px', flexShrink: 0 }} />
                        </>
                    )}
                    <div style={{ margin: 0, padding: 0, minWidth: 0, flex: '1 1 auto', overflowX: 'auto' }}>
                        <TargetDatesSection orgId={orgId} appId={appId} releaseId={releaseId} />
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    {releaseHasActiveRollback && (
                        <Alert
                            type="info"
                            icon={<UndoOutlined />}
                            showIcon
                            message={
                                rollbackInfo.isReadyToRelease ?
                                    `A rollback for this version is ready to release.`
                                :   `A rollback for this version is being prepared.`
                            }
                            action={<Link to={rollbackReleaseRoute}>Go to rollback</Link>}
                        />
                    )}
                    <HighlightsSection
                        appId={appId}
                        releaseId={releaseId}
                        releaseStatsByTypes={releaseStatsByTypes}
                        isLoadingReleaseStatsHistory={isLoadingReleaseStatsHistory}
                        currentReleaseStats={statistics}
                    />
                    <ProductSprintSection
                        appId={appId}
                        releaseId={releaseId}
                        releaseStatsByTypes={releaseStatsByTypes}
                        isLoadingReleaseStatsHistory={isLoadingReleaseStatsHistory}
                        currentReleaseStats={statistics}
                        storeRejectedOn={releaseOverview?.storeRejectedOn}
                        devRejectedOn={releaseOverview?.storeRejectedOn}
                    />
                    <CodeSection
                        appId={appId}
                        releaseId={releaseId}
                        releaseStatsByTypes={releaseStatsByTypes}
                        isLoadingReleaseStatsHistory={isLoadingReleaseStatsHistory}
                        currentReleaseStats={statistics}
                    />
                </div>
            </div>
        </Layout>
    );
};

export default ReleaseOverviewContainer;
